import React from "react";
import {ActionRow, ActionRowButton, Alert, DescriptionList, DescriptionListItem, Modal} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {CartInTimeline, formatTime, isActiveKind} from "@widgets/reports/cartTimeline/CartTimelineUtils";
import CartKindBadge from "@widgets/reports/cartTimeline/status/CartKindBadge";
import CodeFormatter from "@widgets/employees/overview/table/columns/CodeFormatter";
import {globalModalProps} from "@/utils/ModalUtils";
import {formatMoney} from "@/utils/MoneyUtils";
import {paymentLabel} from "@widgets/reports/cartTimeline/order/CartOrderUtils";

type Props = {
    /** The cart to show, `null` when closed. */
    item: CartInTimeline | null,
    onClose: () => void,
};

/**
 * All fields of a cart, opened from a bar in the timeline or a table row.
 */
const CartDetailsModal = ({item, onClose}: Props) => {
    const intl = useIntl();
    const timeOrDash = (date: string | null): string => date === null ? "–" : formatTime(date);

    return (
        <Modal
            {...globalModalProps()}
            show={item !== null}
            onClose={onClose}
            title={intl.formatMessage({id: "cartTimeline.details.title"}, {id: item?.cart.id ?? ""})}
            actionRow={
                <ActionRow>
                    <ActionRowButton
                        color={"secondary"}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    onClose();
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"close"}/>
                    </ActionRowButton>
                </ActionRow>
            }
        >
            {
                item !== null &&
                <div className={"text-left flex flex-col gap-4"}>
                    <Alert
                        type={"primary"}
                        title={<FormattedMessage id={`cartTimeline.explain.${item.kind}`}/>}
                    />
                    <DescriptionList compact={true}>
                        <DescriptionListItem
                            title={intl.formatMessage({id: "status"})}
                            content={<CartKindBadge kind={item.kind}/>}
                        />
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.details.id"})}
                            content={<CodeFormatter code={String(item.cart.id)}/>}
                        />
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.details.publicId"})}
                            content={<CodeFormatter code={item.cart.publicId}/>}
                        />
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.details.userId"})}
                            content={
                                item.cart.userId === null
                                    ? intl.formatMessage({id: "cartTimeline.customer.guest"})
                                    : <CodeFormatter code={String(item.cart.userId)}/>
                            }
                        />
                        {
                            item.cart.order &&
                            <>
                                <DescriptionListItem
                                    title={intl.formatMessage({id: "cartTimeline.details.orderedOn"})}
                                    content={formatTime(item.cart.order.orderedOn)}
                                />
                                <DescriptionListItem
                                    title={intl.formatMessage({id: "cartTimeline.details.orderValue"})}
                                    content={formatMoney(item.cart.order.totalToPay)}
                                />
                                <DescriptionListItem
                                    title={intl.formatMessage({id: "payment"})}
                                    content={paymentLabel(item, item.cart.order, intl)}
                                />
                            </>
                        }
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.details.creationDate"})}
                            content={formatTime(item.cart.creationDate)}
                        />
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.details.expiresOn"})}
                            content={formatTime(item.cart.expiresOn)}
                        />
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.details.emptiedOn"})}
                            content={timeOrDash(item.cart.emptiedOn)}
                        />
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.details.resetOn"})}
                            content={timeOrDash(item.cart.resetOn)}
                        />
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.column.fulfillment"})}
                            content={intl.formatMessage({id: item.cart.isDelivery ? "delivery" : "takeaway"})}
                        />
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.details.apiStatus"})}
                            content={<CodeFormatter code={item.cart.status}/>}
                        />
                        <DescriptionListItem
                            title={intl.formatMessage({id: "cartTimeline.details.active"})}
                            content={intl.formatMessage({id: isActiveKind(item.kind) ? "yes" : "no"})}
                        />
                    </DescriptionList>
                </div>
            }
        </Modal>
    );
};

export default CartDetailsModal;